import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import classnames from 'classnames/bind';

import { useGetDocumentByIdAdmin } from '@SERVICES';

import DESIGN from '@CONSTANTS/DESIGN.constant';

import useDocumentNameModal from '@HOOKS/store/modals/useDocumentNameModal';
import useDocumentIntroductionTextModal from '@HOOKS/store/modals/useDocumentIntroductionTextModal';
import useNewChapterModal from '@HOOKS/store/modals/useNewChapterModal';
import usePublishDocumentModal from '@HOOKS/store/modals/usePublishDocumentModal';
import useDeleteChapterModal from '@HOOKS/store/modals/useDeleteChapterModal';
import useDeleteTagModal from '@HOOKS/store/modals/useDeleteTagModal';
import usePreviewDocumentModal from '@HOOKS/store/modals/usePreviewDocumentModal';
import useNewTagModal from '@HOOKS/store/modals/useNewTagModal';
import useTagNameModal from '@HOOKS/store/modals/useTagNameModal';
import useOrderChaptersDrawer from '@HOOKS/store/drawers/useOrderChaptersDrawer';
import useUnpublishedChangesWarningModal from '@HOOKS/store/modals/useUnpublishedChangesWarningModal';

import { 
    updateDocumentLastOpened, 
    shouldShowUnpublishedWarning 
} from '@HELPERS/documentWarningUtils';

import Content from '@PAGES/admin/document/components/Content';

import DataLoader from '@COMPONENTS/SHARED/DataLoader';
import ContentWithFullHeightSidebar from '@COMPONENTS/SHARED/ContentWithFullHeightSidebar';
import DocumentSidebar from '@COMPONENTS/SIDEBARS/DocumentSidebar';

import styles from './Document.module.scss';

const cx: CX = classnames.bind(styles);

const top = DESIGN['subheader-height'] + DESIGN['admin-breadcrumbs-height'];

function DocumentPage() {
    const params = useParams();

    const { id } = params;

    const {
        data: documentData, isLoading, isFetching, isError, error,
    } = useGetDocumentByIdAdmin(+id!);

    const {
        actions: documentNameModalActions,
    } = useDocumentNameModal(() => true);

    const {
        actions: documentIntroductionTextModalActions,
    } = useDocumentIntroductionTextModal(() => true);

    const {
        actions: newChapterModalActions,
    } = useNewChapterModal(() => true);

    const {
        actions: previewDocumentModalActions,
    } = usePreviewDocumentModal(() => true);

    const {
        actions: publishDocumentModalActions,
    } = usePublishDocumentModal(() => true);

    const {
        actions: deleteChapterModalActions,
    } = useDeleteChapterModal(() => true);

    const {
        actions: deleteTagModalActions,
    } = useDeleteTagModal(() => true);

    const {
        actions: newTagModalActions,
    } = useNewTagModal(() => true);

    const {
        actions: tagNameModalActions,
    } = useTagNameModal(() => true);

    const {
        actions: orderChaptersDrawerActions,
    } = useOrderChaptersDrawer(() => true);

    const {
        actions: unpublishedChangesWarningModalActions,
    } = useUnpublishedChangesWarningModal(() => true);

    // Check if document has unpublished changes
    const hasUnpublishedChanges = useMemo(() => {
        if (!documentData?.data?.chapters) {
            return false;
        }

        const { chapters } = documentData.data;
        return !chapters.every((chapter) => {
            const { introductionText, sections } = chapter;

            return (
                introductionText.isPublished
                && sections.every((section) => {
                    const { baseText, variants } = section;

                    return (
                        variants.every((variant) => variant.isPublished)
                        && baseText.isPublished
                    );
                })
            );
        });
    }, [documentData?.data]);

    // Handle document opened tracking and warning modal
    useEffect(() => {
        if (documentData?.data?.id) {
            const documentId = documentData.data.id;
            
            // Check if warning should be shown before updating the last opened time
            const { shouldShow, daysSinceLastOpened } = shouldShowUnpublishedWarning(
                documentId,
                hasUnpublishedChanges
            );

            if (shouldShow) {
                // Show the warning modal
                unpublishedChangesWarningModalActions.open({
                    documentName: documentData.data.name,
                    lastOpenedDays: daysSinceLastOpened,
                    documentId,
                });
            }

            // Update the last opened time for this document
            updateDocumentLastOpened(documentId);
        }
    }, [documentData?.data?.id, documentData?.data?.name, hasUnpublishedChanges, unpublishedChangesWarningModalActions]);

    // CLOSE MODALS AND DRAWERS
    useEffect(() => () => {
        documentNameModalActions.close();
        documentIntroductionTextModalActions.close();
        newChapterModalActions.close();
        publishDocumentModalActions.close();
        previewDocumentModalActions.close();
        deleteChapterModalActions.close();
        deleteTagModalActions.close();
        newTagModalActions.close();
        tagNameModalActions.close();
        orderChaptersDrawerActions.close();
        unpublishedChangesWarningModalActions.close();
    }, [
        id,
        documentNameModalActions,
        documentIntroductionTextModalActions,
        newChapterModalActions,
        publishDocumentModalActions,
        previewDocumentModalActions,
        deleteChapterModalActions,
        deleteTagModalActions,
        newTagModalActions,
        tagNameModalActions,
        orderChaptersDrawerActions,
        unpublishedChangesWarningModalActions,
    ]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (isError && (error as FetchBaseQueryError)?.status === 404) {
        return (
            <div className={cx('document-not-found', 'container')}>
                404 Document not found
            </div>
        );
    }

    return (
        <div className={cx('page')}>
            <div className="max-constraint center">
                <ContentWithFullHeightSidebar
                    top={top}
                    enableToggle
                    toggleStorageKey="documentSidebarCollapsed"
                    sidebar={(
                        <DocumentSidebar
                            permission={documentData?.meta?.permissions || []}
                            document={{
                                documentOwnerId: documentData?.data?.owner?.id || 0,
                                documentId: documentData?.data?.id || 0,
                            }}
                            documents={documentData?.meta?.documents || []}
                            chapters={documentData?.data?.chapters || []}
                        />
                    )}
                >
                    <DataLoader
                        isLoading={isLoading}
                        isFetching={isFetching}
                        isError={isError}
                        error={error}
                    >
                        {documentData && (
                            <Content
                                key={documentData.data?.id || 0}
                                documentData={documentData}
                                top={top}
                            />
                        )}
                    </DataLoader>
                </ContentWithFullHeightSidebar>
            </div>
        </div>
    );
}

export default DocumentPage;
